import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Skeleton,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Grid,
  Alert,
  Card,
  CardContent,
  Divider,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Stack,
  Fab,
  Collapse,
  Menu,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ListSkeleton } from './SkeletonLoaders';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Send,
  CheckCircle,
  Cancel,
  Receipt,
  FilterList,
  ExpandMore,
  ExpandLess,
  FileDownload,
  PictureAsPdf,
  TableChart,
  Description,
  Autorenew,
  AttachMoney,
  CloudUpload,
  InfoOutlined,
  RestartAlt,
} from '@mui/icons-material';
import {
  getInvoices,
  getInvoice,
  getInvoiceEntries,
  createInvoice,
  updateInvoice,
  updateInvoiceStatus,
  recalculateInvoice,
  recordInvoicePayment,
  listInvoicePayments,
  voidInvoicePayment,
  deleteInvoice,
  getCompanies,
  getCompanyContacts,
  createCompanyContact,
  getTenantSettings,
  exportInvoice,
  getZohoStatus,
  pushInvoiceToZoho,
} from '../api';
import { formatCurrency } from '../utils/formatUtils';
import { useAuth } from '../context/AuthContext';
import { passesRequireAdmin } from '../utils/endpointAccess';
import { getTenantTimezone, todayInTimezone } from '../utils/formatTime';
import { useConfirmDialog } from './ConfirmDialog';
import StatusChip from './StatusChip';
import { useTerms } from '../context/TerminologyContext';

const EXPORT_FORMAT_OPTIONS = [
  { value: 'standard', label: 'PDF Invoice' },
  { value: 'excel', label: 'Excel Invoice' },
  { value: 'ferc', label: 'FERC Format (Excel)' },
];

export default function Invoices() {
  const { t } = useTerms();
  const { user } = useAuth();
  const canReadAdminSettings = passesRequireAdmin(user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  // Form state
  const [formData, setFormData] = useState(() => ({
    company_id: '',
    invoice_date: todayInTimezone(getTenantTimezone()),
    due_date: '',
    start_date: '',
    end_date: '',
    terms: 'Due on Receipt',
    subject: '',
    bill_to_name: '',
    notes: '',
    export_format: '',
    items: []
  }));

  // Entries selection for new invoice
  const [availableEntries, setAvailableEntries] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    company_id: '',
    status: ''
  });

  // Zoho state — whether per-row push button should be visible at all
  const [zohoReady, setZohoReady] = useState(false);
  const [pushingId, setPushingId] = useState(null);

  // Tenant-wide default terms — used as the fallback when a customer has no
  // payment_terms set. Loaded once on mount; defaults to "Due on Receipt"
  // if the API call fails or the tenant has nothing configured.
  const [tenantDefaultTerms, setTenantDefaultTerms] = useState('Due on Receipt');

  // Contacts under the currently-selected client. Populates the Bill To
  // dropdown so the user picks an existing contact instead of free-typing
  // a name (and risking typos that don't match what's on the client record).
  const [companyContacts, setCompanyContacts] = useState([]);
  const [newContactDialogOpen, setNewContactDialogOpen] = useState(false);
  const [newContactForm, setNewContactForm] = useState({
    name: '', email: '', phone: '', role: '', is_primary: false,
  });
  const [newContactSubmitting, setNewContactSubmitting] = useState(false);

  useEffect(() => {
    // Zoho status and the tenant default terms are admin-only reads
    // (requireAdmin). Everyone else keeps the hidden Push button and the
    // 'Due on Receipt' fallback without sending a request that answers 403.
    if (!canReadAdminSettings) {
      setZohoReady(false);
      return;
    }
    getZohoStatus()
      .then(res => setZohoReady(!!(res.data?.connected && res.data?.organizationId)))
      .catch(() => setZohoReady(false));

    // Pull the tenant-level default invoice terms so the form fallback chain
    // (company.payment_terms → tenant.default_invoice_terms) lines up with
    // what pushInvoice will ship to Zoho.
    getTenantSettings()
      .then(res => {
        if (res.data?.default_invoice_terms) setTenantDefaultTerms(res.data.default_invoice_terms);
      })
      .catch(() => {});
  }, [canReadAdminSettings]);

  // Auto-fill the Terms field on the Create Invoice form when the company
  // picker changes — uses the per-customer default (companies.payment_terms)
  // and falls back to the tenant default. Only runs in create mode so that
  // editing an existing invoice doesn't stomp on a manually-set Net 60 etc.
  useEffect(() => {
    if (editMode) return;
    if (!formData.company_id) return;
    const company = companies.find(c => String(c.id) === String(formData.company_id));
    if (!company) return;
    const fillTerms = company.payment_terms || tenantDefaultTerms;
    const fillFormat = company.default_export_format || 'standard';
    setFormData(prev => {
      const next = { ...prev };
      if (fillTerms && prev.terms !== fillTerms) next.terms = fillTerms;
      if (prev.export_format !== fillFormat) next.export_format = fillFormat;
      return (next.terms === prev.terms && next.export_format === prev.export_format) ? prev : next;
    });
  }, [formData.company_id, companies, editMode, tenantDefaultTerms]);

  // Load contacts for the selected client so the Bill To dropdown can show
  // them. Clears when no client is picked so a stale list from the previous
  // client doesn't bleed in.
  useEffect(() => {
    if (!formData.company_id) {
      setCompanyContacts([]);
      return;
    }
    getCompanyContacts(formData.company_id)
      .then(res => setCompanyContacts(res.data || []))
      .catch(() => setCompanyContacts([]));
  }, [formData.company_id]);

  // Keep the Due Date field in sync with Terms + Invoice Date so the user
  // sees the same value the server will compute on submit. Mirrors the
  // server-side derivation in deriveDueDate (server/utils/invoiceTerms.js).
  // Free-text terms that don't parse leave the existing due_date alone.
  useEffect(() => {
    if (!formData.invoice_date || !formData.terms) return;
    const t = String(formData.terms).toLowerCase().trim();
    let days = null;
    if (t.includes('due on receipt') || t.includes('due upon receipt')) days = 0;
    else {
      const m = t.match(/net\s*(\d+)/) || t.match(/^(\d+)$/);
      if (m) days = parseInt(m[1], 10);
    }
    if (days === null) return;
    const d = new Date(formData.invoice_date + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    const derived = d.toISOString().split('T')[0];
    setFormData(prev => (prev.due_date === derived ? prev : { ...prev, due_date: derived }));
  }, [formData.terms, formData.invoice_date]);

  const handlePushToZoho = async (invoice) => {
    setPushingId(invoice.id);
    setError('');
    try {
      const res = await pushInvoiceToZoho(invoice.id);
      const pushedNumber = res.data.invoice_number || invoice.invoice_number;
      const renamed = res.data.adopted_number
        ? ` — renumbered to ${res.data.adopted_number} to match your Zoho sequence`
        : '';
      const warning = res.data.warning ? ` Warning: ${res.data.warning}` : '';
      setSuccess(`Invoice ${pushedNumber} pushed to Zoho (Zoho ID ${res.data.zohoId})${renamed}.${warning}`);
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to push invoice to Zoho');
    } finally {
      setPushingId(null);
    }
  };

  useEffect(() => {
    loadInvoices();
    loadCompanies();

    // Check for pre-selected entries from Timesheets page
    const preselected = sessionStorage.getItem('invoicePreselectedEntries');
    if (preselected) {
      sessionStorage.removeItem('invoicePreselectedEntries');
      try {
        const entries = JSON.parse(preselected);
        if (entries.length > 0) {
          // Determine company and date range from entries
          const companyId = entries[0].company_id;
          const dates = entries.map(e => e.entry_date?.split('T')[0]).filter(Boolean).sort();
          setFormData(prev => ({
            ...prev,
            company_id: companyId || '',
            invoice_date: todayInTimezone(getTenantTimezone()),
            start_date: dates[0] || '',
            end_date: dates[dates.length - 1] || '',
          }));
          setAvailableEntries(entries);
          setSelectedEntries(entries);
          setEditMode(false);
          setCurrentInvoice(null);
          setOpenDialog(true);
        }
      } catch (e) {
        console.error('Failed to parse preselected entries:', e);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.status) params.status = filters.status;
      const res = await getInvoices(params);
      setInvoices(res.data);
    } catch (err) {
      setError('Failed to load invoices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const res = await getCompanies();
      setCompanies(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadEntriesForCompany = async (companyId, startDate, endDate) => {
    if (!companyId) {
      setAvailableEntries([]);
      return;
    }
    setLoadingEntries(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await getInvoiceEntries(companyId, params);
      setAvailableEntries(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingEntries(false);
    }
  };

  const handleOpenDialog = (invoice = null) => {
    if (invoice) {
      setEditMode(true);
      setCurrentInvoice(invoice);
      // Load invoice details
      loadInvoiceDetails(invoice.id);
    } else {
      setEditMode(false);
      setCurrentInvoice(null);
      setFormData({
        company_id: '',
        invoice_date: todayInTimezone(getTenantTimezone()),
        due_date: '',
        start_date: '',
        end_date: '',
        terms: 'Due on Receipt',
        subject: '',
        bill_to_name: '',
        notes: '',
        export_format: '',
        items: []
      });
      setAvailableEntries([]);
      setSelectedEntries([]);
    }
    setOpenDialog(true);
  };

  const loadInvoiceDetails = async (id) => {
    try {
      const res = await getInvoice(id);
      const invoice = res.data;
      setFormData({
        company_id: invoice.company_id,
        invoice_date: invoice.invoice_date?.split('T')[0] || '',
        due_date: invoice.due_date?.split('T')[0] || '',
        start_date: invoice.start_date?.split('T')[0] || '',
        end_date: invoice.end_date?.split('T')[0] || '',
        terms: invoice.terms || 'Due on Receipt',
        subject: invoice.subject || '',
        bill_to_name: invoice.bill_to_name || '',
        notes: invoice.notes || '',
        export_format: invoice.export_format || '',
        items: invoice.items || []
      });
    } catch (err) {
      setError('Failed to load invoice details');
      console.error(err);
    }
  };

  const handleViewInvoice = async (invoice) => {
    try {
      const res = await getInvoice(invoice.id);
      setCurrentInvoice(res.data);
      setOpenViewDialog(true);
      refreshPaymentHistory(invoice.id);
    } catch (err) {
      setError('Failed to load invoice details');
      console.error(err);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setCurrentInvoice(null);
    setPaymentHistory([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Load entries when company or dates change
    if (name === 'company_id' || name === 'start_date' || name === 'end_date') {
      const companyId = name === 'company_id' ? value : formData.company_id;
      const startDate = name === 'start_date' ? value : formData.start_date;
      const endDate = name === 'end_date' ? value : formData.end_date;
      if (companyId) {
        loadEntriesForCompany(companyId, startDate, endDate);
      }
    }
  };

  const handleToggleEntry = (entry) => {
    const isSelected = selectedEntries.some(e => e.id === entry.id);
    if (isSelected) {
      setSelectedEntries(prev => prev.filter(e => e.id !== entry.id));
    } else {
      setSelectedEntries(prev => [...prev, entry]);
    }
  };

  const handleSelectAllEntries = () => {
    if (selectedEntries.length === availableEntries.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries([...availableEntries]);
    }
  };

  // Bill To dropdown helpers — a special "__new__" sentinel value opens an
  // inline create-contact dialog so the user can add a new contact for the
  // selected client without leaving the Create Invoice flow.
  const handleBillToChange = (e) => {
    const value = e.target.value;
    if (value === '__new__') {
      setNewContactForm({ name: '', email: '', phone: '', role: '', is_primary: false });
      setNewContactDialogOpen(true);
      return;
    }
    setFormData(prev => ({ ...prev, bill_to_name: value }));
  };

  const handleSubmitNewContact = async () => {
    if (!formData.company_id) return;
    if (!newContactForm.name.trim()) {
      setError('Contact name is required');
      return;
    }
    if (!newContactForm.email.trim()) {
      setError('Contact email is required');
      return;
    }
    setNewContactSubmitting(true);
    try {
      const res = await createCompanyContact(formData.company_id, {
        name: newContactForm.name.trim(),
        email: newContactForm.email.trim(),
        phone: newContactForm.phone.trim() || null,
        role: newContactForm.role.trim() || null,
        is_primary: !!newContactForm.is_primary,
      });
      const created = res.data;
      // Refresh the dropdown list and auto-select the new contact.
      const refreshed = await getCompanyContacts(formData.company_id);
      setCompanyContacts(refreshed.data || []);
      setFormData(prev => ({ ...prev, bill_to_name: created.name }));
      setNewContactDialogOpen(false);
      setSuccess(`Added contact "${created.name}"`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create contact');
    } finally {
      setNewContactSubmitting(false);
    }
  };

  // Manual line item helpers — for charges that aren't tied to a time entry
  // (consulting fees, materials, retainers, throwaway test invoices, etc.).
  // Server already accepts items with `time_entry_id: null`; this just exposes
  // the existing capability in the UI.
  const handleAddManualItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          time_entry_id: null,
          description: '',
          quantity: 1,
          unit_price: 0,
          amount: 0,
        },
      ],
    }));
  };

  const handleManualItemChange = (idx, field, value) => {
    setFormData(prev => {
      const items = prev.items.map((item, i) => {
        if (i !== idx) return item;
        const next = { ...item, [field]: value };
        // Recompute amount when qty or rate changes; never recompute when the
        // user is editing description (avoids surprising amount changes).
        if (field === 'quantity' || field === 'unit_price') {
          const q = parseFloat(field === 'quantity' ? value : next.quantity) || 0;
          const r = parseFloat(field === 'unit_price' ? value : next.unit_price) || 0;
          next.amount = parseFloat((q * r).toFixed(2));
        }
        return next;
      });
      return { ...prev, items };
    });
  };

  const handleRemoveManualItem = (idx) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx),
    }));
  };

  // Visible manual items (no time_entry_id). In edit mode, formData.items also
  // contains time-entry-linked rows loaded from the server — keep those hidden
  // from the manual-items table but preserve them through submit.
  const manualItemsView = formData.items.filter(item => !item.time_entry_id);

  const calculateManualItemsTotal = () => {
    return manualItemsView.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const handleSubmit = async () => {
    try {
      let allItems;
      if (editMode) {
        // Edit mode: trust formData.items as the canonical list (manual items
        // edited inline plus time-entry items preserved from load). Sanitize
        // numeric fields so the server gets numbers, not strings from inputs.
        allItems = formData.items.map(item => ({
          time_entry_id: item.time_entry_id || null,
          description: item.description || '',
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          amount: parseFloat(item.amount) || 0,
        }));
      } else {
        // Create mode: time-entry rows carry only the entry id — the server
        // prices them at the client bill rate and words them per the tenant's
        // Invoice Line Format. Append the user-added manual rows from formData.items.
        const timeEntryItems = selectedEntries.map(entry => ({
          time_entry_id: entry.id,
        }));
        const manualItems = manualItemsView.map(item => ({
          time_entry_id: null,
          description: item.description || '',
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          amount: parseFloat(item.amount) || 0,
        }));
        allItems = [...timeEntryItems, ...manualItems];
      }

      if (allItems.length === 0) {
        setError('Add at least one time entry or manual line item before saving.');
        return;
      }

      const invoiceData = {
        ...formData,
        items: allItems,
      };

      if (editMode) {
        await updateInvoice(currentInvoice.id, invoiceData);
        setSuccess('Invoice updated successfully');
      } else {
        await createInvoice(invoiceData);
        setSuccess('Invoice created successfully');
      }
      handleCloseDialog();
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save invoice');
      console.error(err);
    }
  };

  const handleStatusChange = async (invoice, newStatus) => {
    try {
      await updateInvoiceStatus(invoice.id, newStatus);
      setSuccess(`Invoice marked as ${newStatus}`);
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update invoice status');
      console.error(err);
    }
  };

  // Record Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount_paid: '', payment_date: '', method: '', reference: '', notes: '',
  });
  // Prevent double-submit when the user rapidly clicks "Record" — otherwise
  // two concurrent POSTs land as two separate payment rows.
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  // Payment history shown in the view dialog
  const [paymentHistory, setPaymentHistory] = useState([]);

  const handleOpenPaymentDialog = (invoice) => {
    setPaymentInvoice(invoice);
    const outstanding = (parseFloat(invoice.total_amount || 0) - parseFloat(invoice.amount_paid || 0)).toFixed(2);
    setPaymentForm({
      amount_paid: outstanding,
      payment_date: todayInTimezone(getTenantTimezone()),
      method: '',
      reference: '',
      notes: '',
    });
    setPaymentDialogOpen(true);
  };

  const handleSubmitPayment = async () => {
    if (!paymentInvoice || paymentSubmitting) return;
    const amount = parseFloat(paymentForm.amount_paid);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Enter a positive payment amount');
      return;
    }
    setPaymentSubmitting(true);
    try {
      await recordInvoicePayment(paymentInvoice.id, {
        amount_paid: amount,
        payment_date: paymentForm.payment_date || null,
        method: paymentForm.method || null,
        reference: paymentForm.reference || null,
        notes: paymentForm.notes || null,
      });
      setSuccess(`Payment recorded on ${paymentInvoice.invoice_number}`);
      setPaymentDialogOpen(false);
      setPaymentInvoice(null);
      // If the view dialog is open for this invoice, refresh the history
      if (currentInvoice?.id === paymentInvoice.id) {
        refreshPaymentHistory(paymentInvoice.id);
        loadInvoiceDetails(paymentInvoice.id);
      }
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const refreshPaymentHistory = async (invoiceId) => {
    try {
      const res = await listInvoicePayments(invoiceId);
      setPaymentHistory(res.data || []);
    } catch (err) {
      // Non-fatal — detail view still works without the history panel
      setPaymentHistory([]);
    }
  };

  const handleVoidPayment = async (paymentId) => {
    if (!currentInvoice) return;
    const confirmed = await showConfirm({
      title: 'Void Payment',
      message: 'Void this payment? The amount will be removed from the invoice total. If the invoice was marked paid, it will revert to sent.',
      confirmText: 'Void',
      confirmColor: 'error',
      severity: 'warning',
    });
    if (!confirmed) return;
    try {
      await voidInvoicePayment(currentInvoice.id, paymentId);
      setSuccess('Payment voided');
      refreshPaymentHistory(currentInvoice.id);
      loadInvoiceDetails(currentInvoice.id);
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to void payment');
    }
  };

  const handleRecalculate = async (invoice) => {
    const confirmed = await showConfirm({
      title: 'Recalculate Invoice',
      message: `Rebuild ${invoice.invoice_number} from its source time entries? Bill rates will be re-resolved against the current company/matrix config, descriptions refreshed, and the subtotal recomputed. The invoice number, dates, and status stay the same.`,
      confirmText: 'Recalculate',
      confirmColor: 'primary',
      severity: 'info'
    });
    if (!confirmed) return;
    try {
      await recalculateInvoice(invoice.id);
      setSuccess(`Invoice ${invoice.invoice_number} recalculated`);
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to recalculate invoice');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm({
      title: 'Delete Invoice',
      message: 'Are you sure you want to delete this invoice? This action cannot be undone.',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await deleteInvoice(id);
      setSuccess('Invoice deleted successfully');
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete invoice');
      console.error(err);
    }
  };

  // Display status: a sent invoice past its due date reads as Overdue
  // (derived in the UI only — the stored status stays 'sent').
  const todayStr = todayInTimezone(getTenantTimezone()); // once per render, shared by every row
  const displayStatus = (invoice, today = todayStr) => {
    if (invoice?.status === 'sent' && invoice.due_date) {
      const due = invoice.due_date.split('T')[0];
      if (due < today) return 'overdue';
    }
    return invoice?.status;
  };


  const calculateSelectedTotal = () => {
    return selectedEntries.reduce((sum, entry) => sum + parseFloat(entry.invoice_amount || 0), 0);
  };

  // Mobile Card View for invoices
  const renderMobileView = () => (
    <Stack spacing={1.5}>
      {loading ? (
        <ListSkeleton count={4} />
      ) : invoices.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No invoices found</Typography>
        </Box>
      ) : (
        invoices.map(invoice => (
          <Card key={invoice.id} variant="outlined">
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              {/* Header with invoice number and status */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {invoice.invoice_number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {invoice.company_name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                  <StatusChip status={displayStatus(invoice)} />
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {formatCurrency(invoice.total_amount)}
                  </Typography>
                </Box>
              </Box>

              {/* Details */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover', borderRadius: 1, px: 1, py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Date:</Typography>
                  <Typography variant="caption" fontWeight="medium">{invoice.invoice_date?.split('T')[0]}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover', borderRadius: 1, px: 1, py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Period:</Typography>
                  <Typography variant="caption" fontWeight="medium">
                    {invoice.start_date?.split('T')[0]} - {invoice.end_date?.split('T')[0]}
                  </Typography>
                </Box>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1.5, mt: 1, flexWrap: 'wrap' }}>
                <Button size="small" startIcon={<Visibility />} onClick={() => handleViewInvoice(invoice)}>
                  View
                </Button>
                {zohoReady && invoice.status !== 'cancelled' && (
                  <Tooltip title="Push this invoice to your connected Zoho Books org. Creates the Zoho contact and invoice on first push, updates them on subsequent pushes.">
                    <span>
                      <Button
                        size="small"
                        startIcon={<CloudUpload />}
                        disabled={pushingId === invoice.id}
                        onClick={() => handlePushToZoho(invoice)}
                      >
                        {pushingId === invoice.id ? 'Pushing…' : 'Push to Zoho'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {invoice.status === 'draft' && (
                  <>
                    <Button size="small" startIcon={<Edit />} onClick={() => handleOpenDialog(invoice)}>
                      Edit
                    </Button>
                    <Tooltip title="Re-resolve bill rates against the current company/matrix config, refresh line descriptions from the source time entries, and roll up the new subtotal. Keeps the invoice number and dates.">
                      <Button size="small" startIcon={<Autorenew />} onClick={() => handleRecalculate(invoice)}>
                        Recalc
                      </Button>
                    </Tooltip>
                    <Button size="small" color="primary" startIcon={<Send />} onClick={() => handleStatusChange(invoice, 'sent')}>
                      Send
                    </Button>
                    <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDelete(invoice.id)}>
                      Delete
                    </Button>
                  </>
                )}
                {invoice.status === 'sent' && (
                  <>
                    <Button size="small" color="success" startIcon={<AttachMoney />} onClick={() => handleOpenPaymentDialog(invoice)}>
                      Payment
                    </Button>
                    <Button size="small" color="success" startIcon={<CheckCircle />} onClick={() => handleStatusChange(invoice, 'paid')}>
                      Paid
                    </Button>
                    <Button size="small" color="error" startIcon={<Cancel />} onClick={() => handleStatusChange(invoice, 'cancelled')}>
                      Cancel
                    </Button>
                  </>
                )}
                {invoice.status === 'cancelled' && (
                  <Tooltip title="Reopen this cancelled invoice as a draft so it can be edited, recalculated, or pushed to Zoho. Its time entries stay linked.">
                    <Button size="small" startIcon={<RestartAlt />} onClick={() => handleStatusChange(invoice, 'draft')}>
                      Reopen as Draft
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </CardContent>
          </Card>
        ))
      )}
    </Stack>
  );

  // Desktop Table View
  const renderDesktopView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Invoice #</TableCell>
            <TableCell>{t('client.singular')}</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Period</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                <TableCell align="right"><Skeleton variant="text" width="50%" /></TableCell>
                <TableCell><Skeleton variant="rounded" width={60} height={24} /></TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Skeleton variant="circular" width={28} height={28} />
                    <Skeleton variant="circular" width={28} height={28} />
                    <Skeleton variant="circular" width={28} height={28} />
                  </Box>
                </TableCell>
              </TableRow>
            ))
          ) : invoices.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">No invoices found</TableCell>
            </TableRow>
          ) : (
            invoices.map(invoice => (
              <TableRow key={invoice.id} hover>
                <TableCell>{invoice.invoice_number}</TableCell>
                <TableCell>{invoice.company_name}</TableCell>
                <TableCell>{invoice.invoice_date?.split('T')[0]}</TableCell>
                <TableCell>
                  {invoice.start_date?.split('T')[0]} - {invoice.end_date?.split('T')[0]}
                </TableCell>
                <TableCell align="right">{formatCurrency(invoice.total_amount)}</TableCell>
                <TableCell>
                  <StatusChip status={displayStatus(invoice)} />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View">
                    <IconButton size="small" onClick={() => handleViewInvoice(invoice)}>
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  {zohoReady && invoice.status !== 'cancelled' && (
                    <Tooltip title="Push to Zoho Books — creates the contact and invoice on first push, updates them on subsequent pushes.">
                      <span>
                        <IconButton
                          size="small"
                          disabled={pushingId === invoice.id}
                          onClick={() => handlePushToZoho(invoice)}
                        >
                          <CloudUpload />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {invoice.status === 'draft' && (
                    <>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenDialog(invoice)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Recalculate: re-resolves bill rates against the current company/matrix config and refreshes line descriptions. Keeps invoice number.">
                        <IconButton size="small" onClick={() => handleRecalculate(invoice)}>
                          <Autorenew />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mark as Sent">
                        <IconButton size="small" color="primary" onClick={() => handleStatusChange(invoice, 'sent')}>
                          <Send />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(invoice.id)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {invoice.status === 'sent' && (
                    <>
                      <Tooltip title="Record Payment">
                        <IconButton size="small" color="success" onClick={() => handleOpenPaymentDialog(invoice)}>
                          <AttachMoney />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mark as Paid (full amount)">
                        <IconButton size="small" color="success" onClick={() => handleStatusChange(invoice, 'paid')}>
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton size="small" color="error" onClick={() => handleStatusChange(invoice, 'cancelled')}>
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {invoice.status === 'cancelled' && (
                    <Tooltip title="Reopen as Draft — makes this cancelled invoice editable again (edit, recalculate, push to Zoho). Its time entries stay linked.">
                      <IconButton size="small" onClick={() => handleStatusChange(invoice, 'draft')}>
                        <RestartAlt />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ pb: isMobile ? 10 : 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ display: 'flex', alignItems: 'center' }}>
          <Receipt sx={{ mr: 1 }} />
          Invoices
        </Typography>
        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Create Invoice
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Mobile Filter Toggle */}
      {isMobile && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<FilterList />}
          endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{ mb: 2 }}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      )}

      {/* Filters and Actions */}
      <Collapse in={!isMobile || showFilters}>
        <Paper sx={{ p: isMobile ? 2 : 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                select
                label={`Filter by ${t('client.singular')}`}
                value={filters.company_id}
                onChange={(e) => setFilters({ ...filters, company_id: e.target.value })}
                size="small"
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">{`All ${t('client.plural')}`}</MenuItem>
                {companies.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                select
                label="Filter by Status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                size="small"
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button variant="outlined" onClick={loadInvoices} fullWidth>
                Apply
              </Button>
            </Grid>
            {!isMobile && (
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog()}
                  fullWidth
                  sx={{ display: { xs: 'none', sm: 'none' } }}
                >
                  Create Invoice
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Collapse>

      {/* Invoice List */}
      {isMobile ? renderMobileView() : renderDesktopView()}

      {/* Mobile FAB for adding invoice */}
      {isMobile && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 72, right: 16 }}
          onClick={() => handleOpenDialog()}
        >
          <Add />
        </Fab>
      )}

      {/* Create/Edit Invoice Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        aria-labelledby="invoice-dialog-title"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      >
        <DialogTitle id="invoice-dialog-title">{editMode ? 'Edit Invoice' : 'Create New Invoice'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label={t('client.singular')}
                name="company_id"
                value={formData.company_id}
                onChange={handleInputChange}
                required
                size={isMobile ? 'small' : 'medium'}
              >
                {companies.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Invoice Date"
                name="invoice_date"
                value={formData.invoice_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Due Date"
                name="due_date"
                value={formData.due_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Period Start"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Period End"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Terms"
                name="terms"
                value={formData.terms}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                helperText="Determines the due date and what prints on the invoice PDF"
              >
                {['Due on Receipt', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90'].map(term => (
                  <MenuItem key={term} value={term}>{term}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Invoice Format"
                name="export_format"
                value={formData.export_format || 'standard'}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                helperText={`The document this invoice produces — set when it's created, defaulted from the ${t('client.singular', { lower: true })}`}
              >
                {EXPORT_FORMAT_OPTIONS.map(f => (
                  <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Bill To (Contact)"
                name="bill_to_name"
                value={formData.bill_to_name || ''}
                onChange={handleBillToChange}
                disabled={!formData.company_id}
                InputLabelProps={{ shrink: true }}
                helperText={
                  !formData.company_id
                    ? `Pick the ${t('client.singular', { lower: true })} first`
                    : `Shown under 'Bill To' on the invoice PDF. Leave blank to use the ${t('client.singular', { lower: true })}'s primary contact.`
                }
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  <em>— Use primary contact —</em>
                </MenuItem>
                {companyContacts.map(c => (
                  <MenuItem key={c.id} value={c.name}>
                    {c.name}
                    {c.is_primary ? ' (primary)' : ''}
                    {c.role ? ` — ${c.role}` : ''}
                  </MenuItem>
                ))}
                {/* Preserve a legacy / free-typed bill_to_name from before
                    the dropdown change, so editing an old invoice doesn't
                    silently lose the stored value. */}
                {formData.bill_to_name &&
                  !companyContacts.some(c => c.name === formData.bill_to_name) && (
                    <MenuItem value={formData.bill_to_name}>
                      {formData.bill_to_name} (legacy)
                    </MenuItem>
                  )}
                <Divider />
                {formData.company_id && (
                  <MenuItem value="__new__">
                    <Add fontSize="small" sx={{ mr: 1 }} />
                    Add new contact…
                  </MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="Short one-line title (e.g. IT Consulting - March 2026)"
                inputProps={{ maxLength: 255 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                multiline
                rows={2}
                placeholder="Appears at the bottom of the invoice (e.g. 'Thanks for your business.')"
              />
            </Grid>

            {/* Manual Line Items — non-time-entry charges (consulting fees,
                materials, retainers, throwaway test invoices, etc.). Always
                available, in both create and edit mode. */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="h6">Manual Line Items</Typography>
                  <Tooltip title="Free-form line items not tied to a time entry — useful for consulting fees, materials, retainers, or throwaway test invoices. Combined with any time entries selected below.">
                    <InfoOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                  </Tooltip>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddManualItem}
                >
                  Add Item
                </Button>
              </Box>
              {manualItemsView.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No manual items. Click "Add Item" to add a custom line.
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 200 }}>Description</TableCell>
                          <TableCell align="right" sx={{ width: 90 }}>Qty</TableCell>
                          <TableCell align="right" sx={{ width: 110 }}>Rate</TableCell>
                          <TableCell align="right" sx={{ width: 110 }}>Amount</TableCell>
                          <TableCell align="center" sx={{ width: 50 }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.items.map((item, idx) => {
                          if (item.time_entry_id) return null;
                          return (
                            <TableRow key={idx}>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={item.description || ''}
                                  onChange={(e) => handleManualItemChange(idx, 'description', e.target.value)}
                                  placeholder="e.g. Consulting fee — March engagement"
                                  inputProps={{ maxLength: 500 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  size="small"
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => handleManualItemChange(idx, 'quantity', e.target.value)}
                                  inputProps={{ step: '0.01', min: 0, style: { textAlign: 'right' } }}
                                  sx={{ width: 80 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  size="small"
                                  type="number"
                                  value={item.unit_price}
                                  onChange={(e) => handleManualItemChange(idx, 'unit_price', e.target.value)}
                                  inputProps={{ step: '0.01', min: 0, style: { textAlign: 'right' } }}
                                  sx={{ width: 100 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(item.amount)}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveManualItem(idx)}
                                  aria-label="Remove line item"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography align="right" variant="subtitle1">
                      Manual items subtotal: {formatCurrency(calculateManualItemsTotal())}
                    </Typography>
                  </Box>
                </>
              )}
            </Grid>

            {/* Time Entries Selection */}
            {!editMode && formData.company_id && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Select Time Entries to Include
                </Typography>
                {loadingEntries ? (
                  <Typography>Loading entries...</Typography>
                ) : availableEntries.length === 0 ? (
                  <Alert severity="info">No unbilled time entries found for this {t('client.singular', { lower: true })} and date range.</Alert>
                ) : (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedEntries.length === availableEntries.length}
                          indeterminate={selectedEntries.length > 0 && selectedEntries.length < availableEntries.length}
                          onChange={handleSelectAllEntries}
                        />
                      }
                      label={`Select All (${availableEntries.length} entries)`}
                    />
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox"></TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Employee</TableCell>
                            <TableCell align="right">Hours</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                Bill Rate
                                <Tooltip title="The client bill rate snapshotted on the entry: the employee × client rate, else the client's Default Bill Rate, else the employee's pay rate. This is what the client is charged, not what the employee is paid.">
                                  <InfoOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell align="right">Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {availableEntries.map(entry => (
                            <TableRow key={entry.id} hover>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedEntries.some(e => e.id === entry.id)}
                                  onChange={() => handleToggleEntry(entry)}
                                />
                              </TableCell>
                              <TableCell>{entry.entry_date?.split('T')[0]}</TableCell>
                              <TableCell>{entry.first_name} {entry.last_name}</TableCell>
                              <TableCell align="right">{parseFloat(entry.hours_worked).toFixed(2)}</TableCell>
                              <TableCell align="right">{formatCurrency(entry.invoice_unit_price)}</TableCell>
                              <TableCell align="right">{formatCurrency(entry.invoice_amount)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography>Selected Entries: {selectedEntries.length}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography align="right" variant="h6">
                            Subtotal: {formatCurrency(calculateSelectedTotal())}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
          <Button onClick={handleCloseDialog} size={isMobile ? 'large' : 'medium'}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.company_id ||
              !formData.start_date ||
              !formData.end_date ||
              (!editMode && selectedEntries.length === 0 && manualItemsView.length === 0)
            }
            size={isMobile ? 'large' : 'medium'}
          >
            {editMode ? 'Update' : 'Create'} Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Contact Dialog — opened from the Bill To dropdown's
          "Add new contact…" item. Creates a contact under the currently
          selected client and auto-selects it as the bill_to_name. */}
      <Dialog
        open={newContactDialogOpen}
        onClose={() => !newContactSubmitting && setNewContactDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Contact</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                fullWidth
                label="Name"
                value={newContactForm.name}
                onChange={(e) => setNewContactForm(prev => ({ ...prev, name: e.target.value }))}
                required
                inputProps={{ maxLength: 255 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newContactForm.email}
                onChange={(e) => setNewContactForm(prev => ({ ...prev, email: e.target.value }))}
                required
                inputProps={{ maxLength: 255 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={newContactForm.phone}
                onChange={(e) => setNewContactForm(prev => ({ ...prev, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Role / Title"
                value={newContactForm.role}
                onChange={(e) => setNewContactForm(prev => ({ ...prev, role: e.target.value }))}
                placeholder="e.g. Accounts Payable, CFO"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newContactForm.is_primary}
                    onChange={(e) => setNewContactForm(prev => ({ ...prev, is_primary: e.target.checked }))}
                  />
                }
                label={`Set as primary contact for this ${t('client.singular', { lower: true })}`}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewContactDialogOpen(false)} disabled={newContactSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitNewContact}
            variant="contained"
            disabled={newContactSubmitting || !newContactForm.name.trim() || !newContactForm.email.trim()}
          >
            {newContactSubmitting ? 'Adding…' : 'Add Contact'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Invoice {currentInvoice?.invoice_number}
          <StatusChip status={displayStatus(currentInvoice)} sx={{ ml: 2 }} />
        </DialogTitle>
        <DialogContent>
          {currentInvoice && (
            <Box>
              {/* Balance Due stamp at top — mirrors the Zoho PDF header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h5">{currentInvoice.invoice_number}</Typography>
                  <StatusChip status={displayStatus(currentInvoice)} sx={{ mt: 0.5 }} />
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Balance Due</Typography>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(Math.max(0, parseFloat(currentInvoice.total_amount || 0) - parseFloat(currentInvoice.amount_paid || 0)))}
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Bill To</Typography>
                  <Typography variant="h6">{currentInvoice.bill_to_name || currentInvoice.company_name}</Typography>
                  {currentInvoice.bill_to_name && currentInvoice.bill_to_name !== currentInvoice.company_name && (
                    <Typography variant="body2" color="textSecondary">{currentInvoice.company_name}</Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Invoice Date</Typography>
                  <Typography>{currentInvoice.invoice_date?.split('T')[0]}</Typography>
                </Grid>
                {currentInvoice.subject && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Subject</Typography>
                    <Typography>{currentInvoice.subject}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Terms</Typography>
                  <Typography>{currentInvoice.terms || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Format</Typography>
                  <Typography>
                    {EXPORT_FORMAT_OPTIONS.find(f => f.value === currentInvoice.export_format)?.label
                      || <em style={{ opacity: 0.6 }}>{t('client.singular')} default</em>}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Due Date</Typography>
                  <Typography>{currentInvoice.due_date?.split('T')[0] || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Period</Typography>
                  <Typography>
                    {currentInvoice.start_date?.split('T')[0]} - {currentInvoice.end_date?.split('T')[0]}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>Line Items</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentInvoice.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ whiteSpace: 'pre-line' }}>{item.description}</TableCell>
                        <TableCell align="right">{parseFloat(item.quantity).toFixed(2)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 3, textAlign: 'right' }}>
                <Typography>Subtotal: {formatCurrency(currentInvoice.subtotal)}</Typography>
                <Typography variant="h5" sx={{ mt: 1 }}>
                  Total: {formatCurrency(currentInvoice.total_amount)}
                </Typography>
                {parseFloat(currentInvoice.amount_paid || 0) > 0 && (
                  <>
                    <Typography sx={{ mt: 1 }} color="error">
                      Payment Made: (-) {formatCurrency(currentInvoice.amount_paid)}
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      Balance Due: {formatCurrency(parseFloat(currentInvoice.total_amount || 0) - parseFloat(currentInvoice.amount_paid || 0))}
                    </Typography>
                  </>
                )}
                {currentInvoice.status === 'paid' && (
                  <StatusChip status="paid" label="PAID" sx={{ mt: 1.5, fontSize: '0.8125rem', padding: '6px 14px' }} />
                )}
              </Box>

              {/* Payment history */}
              {paymentHistory.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Payment History</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>Reference</TableCell>
                          <TableCell>Notes</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paymentHistory.map(p => (
                          <TableRow key={p.id} sx={p.voided_at ? { opacity: 0.5, textDecoration: 'line-through' } : undefined}>
                            <TableCell>{p.payment_date?.split('T')[0]}</TableCell>
                            <TableCell>{p.method || '—'}</TableCell>
                            <TableCell>{p.reference || '—'}</TableCell>
                            <TableCell>{p.notes || '—'}</TableCell>
                            <TableCell align="right">{formatCurrency(p.amount)}</TableCell>
                            <TableCell align="right">
                              {p.voided_at ? (
                                <StatusChip status="voided" />
                              ) : (
                                <Tooltip title="Void this payment">
                                  <IconButton size="small" color="error" onClick={() => handleVoidPayment(p.id)}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {currentInvoice.notes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                  <Typography>{currentInvoice.notes}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
          {currentInvoice && (
            <>
              <Button
                startIcon={<FileDownload />}
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                size={isMobile ? 'large' : 'medium'}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
              >
                <MenuItem onClick={async () => {
                  setExportMenuAnchor(null);
                  try { await exportInvoice(currentInvoice.id, 'standard'); }
                  catch (err) { console.error('PDF export error:', err); }
                }}>
                  <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                  <ListItemText>PDF Invoice</ListItemText>
                </MenuItem>
                <MenuItem onClick={async () => {
                  setExportMenuAnchor(null);
                  try { await exportInvoice(currentInvoice.id, 'excel'); }
                  catch (err) { console.error('Excel export error:', err); }
                }}>
                  <ListItemIcon><TableChart fontSize="small" /></ListItemIcon>
                  <ListItemText>Excel Invoice</ListItemText>
                </MenuItem>
                <MenuItem onClick={async () => {
                  setExportMenuAnchor(null);
                  try { await exportInvoice(currentInvoice.id, 'ferc'); }
                  catch (err) { console.error('FERC export error:', err); }
                }}>
                  <ListItemIcon><Description fontSize="small" /></ListItemIcon>
                  <ListItemText>FERC Format</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
          {currentInvoice && currentInvoice.status !== 'cancelled' &&
           parseFloat(currentInvoice.total_amount || 0) > parseFloat(currentInvoice.amount_paid || 0) && (
            <Button
              variant="contained"
              color="success"
              startIcon={<AttachMoney />}
              onClick={() => handleOpenPaymentDialog(currentInvoice)}
              size={isMobile ? 'large' : 'medium'}
            >
              Record Payment
            </Button>
          )}
          <Button onClick={handleCloseViewDialog} size={isMobile ? 'large' : 'medium'}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {paymentInvoice && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {paymentInvoice.invoice_number} — Total {formatCurrency(paymentInvoice.total_amount)}
                {parseFloat(paymentInvoice.amount_paid || 0) > 0 &&
                  ` (${formatCurrency(paymentInvoice.amount_paid)} already paid)`}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Amount Received"
                    type="number"
                    value={paymentForm.amount_paid}
                    onChange={(e) => setPaymentForm(p => ({ ...p, amount_paid: e.target.value }))}
                    InputProps={{ startAdornment: '$' }}
                    inputProps={{ step: '0.01', min: 0 }}
                    helperText="Amount of THIS payment (not cumulative)"
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Payment Date"
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm(p => ({ ...p, payment_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Method"
                    value={paymentForm.method}
                    onChange={(e) => setPaymentForm(p => ({ ...p, method: e.target.value }))}
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="check">Check</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="ach">ACH / Bank Transfer</MenuItem>
                    <MenuItem value="wire">Wire</MenuItem>
                    <MenuItem value="credit_card">Credit Card</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Reference"
                    placeholder="Check # / transaction ID"
                    value={paymentForm.reference}
                    onChange={(e) => setPaymentForm(p => ({ ...p, reference: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={2}
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm(p => ({ ...p, notes: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)} disabled={paymentSubmitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitPayment} disabled={paymentSubmitting}>
            {paymentSubmitting ? 'Recording…' : 'Record'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialogComponent />
    </Box>
  );
}
